import apiClient from "./apiClient";
import { formatApiError } from "../lib/laravelError";
import {
  assertAccommodationSelected,
  enrichMeetingsWithPlaces,
  formatMeetingCreateError,
  resolveAccommodationId,
} from "../lib/meetingAccommodation";
import { getSessionUser } from "./authSession";
import { accountService } from "./accountService";
import { haversineKm } from "../lib/geo";

/** @typedef {import("../types/meeting").Meeting} Meeting */
/** @typedef {import("../types/meeting").MeetingCreatePayload} MeetingCreatePayload */

/** IDs del seeder `MeetingTypeSeeder`: one_to_one → 1, two_to_two → 2 */
const MEETING_TYPE_ID = {
  "1+1": 1,
  "2+2": 2,
};

const DEFAULT_MEETING_RADIUS_M = 200;

function pickMeetingList(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data.data)) return data.data;
  return [];
}

function unwrapMeeting(data) {
  if (!data || typeof data !== "object") return null;
  return data.data && typeof data.data === "object" && !Array.isArray(data.data)
    ? data.data
    : data;
}

function mapTypeFromApi(meetingType) {
  const name = meetingType?.name;
  if (name === "two_to_two") return "2+2";
  return "1+1";
}

function mapStatusForUi(status) {
  return status === "open" ? "open" : "closed";
}

function mapMeetingFromApi(raw) {
  if (!raw || typeof raw !== "object") return null;
  const lat = raw.latitude != null ? Number(raw.latitude) : null;
  const lng = raw.longitude != null ? Number(raw.longitude) : null;
  const participantsCount = Number(raw.participants_count ?? 0);
  const accommodationId = raw.accommodation_id ?? null;

  return {
    id: raw.id,
    meetingCode: raw.meeting_code ?? null,
    organizerId: raw.organizer?.id ?? null,
    organizerNick: raw.organizer?.name ?? "",
    type: mapTypeFromApi(raw.meeting_type),
    address: raw.address ?? "",
    time: raw.meeting_time,
    identifier: raw.title ?? raw.meeting_code ?? "",
    exactLat: lat,
    exactLng: lng,
    approxLat: lat,
    approxLng: lng,
    radius: raw.radius ?? DEFAULT_MEETING_RADIUS_M,
    accommodationId,
    placeUuid: accommodationId,
    placeName: null,
    placeLocality: null,
    physicalAttributes: [],
    preferences: [],
    interests: [],
    status: mapStatusForUi(raw.status),
    capacity: raw.max_participants ?? 1,
    participants: Array.from({ length: participantsCount }, (_, i) => `participant-${i}`),
    participantsCount,
    distanceKm:
      raw.distance != null && Number.isFinite(Number(raw.distance))
        ? Number(raw.distance) / 1000
        : null,
    canBeEdited: raw.can_be_edited,
    hasParticipants: raw.has_participants,
  };
}

function uiCreateToApi(payload) {
  const meetingTypeId = MEETING_TYPE_ID[payload.type] ?? MEETING_TYPE_ID["1+1"];
  const accommodationId = assertAccommodationSelected(payload);
  return {
    meeting_type_id: meetingTypeId,
    accommodation_id: accommodationId,
    title: payload.identifier?.trim() || null,
    address: payload.address,
    latitude: payload.exactLat,
    longitude: payload.exactLng,
    meeting_time: payload.time,
    radius: DEFAULT_MEETING_RADIUS_M,
    max_participants: payload.type === "2+2" ? 2 : 1,
    status: "open",
  };
}

/** PATCH: accommodation_id no se incluye (solo obligatorio en POST). */
function uiUpdateToApi(payload) {
  const body = {};
  if (payload.identifier != null) body.title = payload.identifier?.trim() || null;
  if (payload.address != null) body.address = payload.address;
  if (payload.exactLat != null) body.latitude = payload.exactLat;
  if (payload.exactLng != null) body.longitude = payload.exactLng;
  if (payload.time != null) body.meeting_time = payload.time;
  if (payload.type != null) {
    body.meeting_type_id = MEETING_TYPE_ID[payload.type] ?? MEETING_TYPE_ID["1+1"];
  }
  return body;
}

function radiusKmToMeters(radiusKm) {
  const km = Math.min(Math.max(Number(radiusKm) || 25, 1), 100);
  return Math.min(Math.round(km * 1000), 100_000);
}

export const meetingService = {
  async listAround({ lat, lng, radiusKm = 25 }) {
    if (typeof lat !== "number" || typeof lng !== "number") {
      return this.listAll();
    }

    try {
      const { data } = await apiClient.get("/meetings/nearby", {
        params: {
          latitude: lat,
          longitude: lng,
          radius: radiusKmToMeters(radiusKm),
          for_map: true,
        },
      });
      return pickMeetingList(data)
        .map(mapMeetingFromApi)
        .filter(Boolean)
        .map((m) => ({
          ...m,
          distanceKm:
            m.distanceKm ??
            (m.approxLat != null && m.approxLng != null
              ? haversineKm({ lat, lng }, { lat: m.approxLat, lng: m.approxLng })
              : null),
        }))
        .sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0));
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudieron cargar los encuentros cercanos"));
    }
  },

  async listAll() {
    const me = getSessionUser();
    if (!me?.id) return [];

    try {
      const { data } = await apiClient.get("/meetings/mine", {
        params: { per_page: 100 },
      });
      return pickMeetingList(data).map(mapMeetingFromApi).filter(Boolean);
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudieron cargar los encuentros"));
    }
  },

  async getById(id) {
    const targetId = String(id);

    try {
      const { data } = await apiClient.get("/meetings/mine", {
        params: { per_page: 100 },
      });
      const mine = pickMeetingList(data).map(mapMeetingFromApi).filter(Boolean);
      const owned = mine.find((m) => String(m.id) === targetId);
      if (owned) return owned;

      const saved = await accountService.getSavedLocation();
      const lat = saved?.lat;
      const lng = saved?.lng;
      if (typeof lat === "number" && typeof lng === "number") {
        const nearby = await this.listAround({ lat, lng, radiusKm: 100 });
        const found = nearby.find((m) => String(m.id) === targetId);
        if (found) return found;
      }

      return null;
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudo cargar el encuentro"));
    }
  },

  async listByOrganizer(userId) {
    const me = getSessionUser();
    if (!me?.id) return [];
    if (userId != null && String(userId) !== String(me.id)) return [];

    try {
      const { data } = await apiClient.get("/meetings/mine", {
        params: { per_page: 100 },
      });
      return pickMeetingList(data).map(mapMeetingFromApi).filter(Boolean);
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudieron cargar tus encuentros"));
    }
  },

  async listCompatible({ profile, lat, lng, radiusKm = 50 }) {
    const me = getSessionUser();
    let latitude = lat;
    let longitude = lng;

    if (typeof latitude !== "number" || typeof longitude !== "number") {
      const saved = await accountService.getSavedLocation();
      latitude = saved?.lat;
      longitude = saved?.lng;
    }

    if (typeof latitude !== "number" || typeof longitude !== "number") {
      return [];
    }

    try {
      const { data } = await apiClient.get("/meetings/nearby", {
        params: {
          latitude,
          longitude,
          radius: radiusKmToMeters(radiusKm),
          per_page: 50,
        },
      });
      return pickMeetingList(data)
        .map(mapMeetingFromApi)
        .filter(Boolean)
        .filter((m) => m.organizerId !== me?.id && m.status === "open");
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudieron cargar encuentros compatibles"));
    }
  },

  async create(payload) {
    const me = getSessionUser();
    if (!me) throw new Error("No autenticado");

    if (!resolveAccommodationId(payload)) {
      throw new Error("Debes seleccionar un hospedaje para publicar el encuentro.");
    }

    try {
      const { data } = await apiClient.post("/meetings", uiCreateToApi(payload));
      const created = mapMeetingFromApi(unwrapMeeting(data));
      if (!created) throw new Error("Respuesta inválida del servidor");
      return {
        ...created,
        placeName: payload.placeName ?? created.placeName,
        placeLocality: payload.placeLocality ?? created.placeLocality,
      };
    } catch (err) {
      if (!err?.response && err?.message) throw err;
      throw new Error(formatMeetingCreateError(err));
    }
  },

  enrichWithPlaces(meetings, places) {
    return enrichMeetingsWithPlaces(meetings, places);
  },

  /** Reservado para edición: no envía accommodation_id. */
  uiUpdateToApi,

  async join(_meetingId) {
    throw new Error("La solicitud de entrada estará disponible próximamente.");
  },

  async myParticipations() {
    return [];
  },

  haversineKm,
};
